import { useMemo } from 'react';
import type { MiraElement } from '@shared/types/mira';
import { FormElementsApiResponse } from './ComponentTypes';
import {
  isMiraFieldForLookupSource,
  MiraLookupSource
} from './miraLookupFields';

type LookupField = {
  id: number;
  miraName: keyof MiraElement;
};

type LookupFieldCandidate = {
  id: number;
  miraName: keyof MiraElement | undefined;
};

type SetValueFunction = (
  name: string,
  value: { value: string },
  options: { shouldValidate: boolean }
) => void;

type MiraLookupResponse = Partial<Record<keyof MiraElement, string>>;

export const useMiraLookupFieldSync = ({
  formData,
  setValue,
  source
}: {
  formData?: FormElementsApiResponse;
  setValue: SetValueFunction;
  source: MiraLookupSource;
}) => {
  const lookupFields = useMemo<LookupField[]>(() => {
    return (
      formData?.elements
        .map((element): LookupFieldCandidate => {
          return {
            id: element.id,
            miraName: element.props?.miraName
          };
        })
        .filter((lookupField): lookupField is LookupField => {
          return isMiraFieldForLookupSource(lookupField.miraName, source);
        }) ?? []
    );
  }, [formData?.elements, source]);

  const clearLookupFields = () => {
    lookupFields.forEach(({ id }) => {
      setValue(id.toString(), { value: '' }, { shouldValidate: true });
    });
  };

  const setLookupFieldsFromResponse = (
    lookupResponse: MiraLookupResponse,
    formatter?: (fieldName: keyof MiraElement, value: string) => string
  ) => {
    lookupFields.forEach(({ id, miraName }) => {
      const rawValue = lookupResponse[miraName];
      if (rawValue !== undefined && rawValue !== null) {
        const valueToSet = formatter ? formatter(miraName, rawValue) : rawValue;
        setValue(
          id.toString(),
          {
            value: valueToSet
          },
          {
            shouldValidate: true
          }
        );
      }
    });
  };

  return {
    clearLookupFields,
    setLookupFieldsFromResponse
  };
};
